import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Loader2, ArrowDown, Info } from "lucide-react";
import { MessageItem } from "./MessageItem";
import { dayKey, dayLabel, fmtTime } from "../../lib/chat";

/* 消息流：日期分隔 · 上翻加载（节流 + 防重复 + 保持滚动位置 + 首屏不满自动补页）· 新消息贴底 · 定位高亮 2 秒 */
const TOP_TRIGGER = 120;   // 距顶 120px 内触发加载更早一页
const THROTTLE_MS = 300;
export const MessageList = ({ msgs, hasMore, onMore, focusId, typing, zh, selectedIds, onJumpMissing, chType, ...rest }) => {
  const box = useRef(null);
  const [stick, setStick] = useState(true);
  const [hl, setHl] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastLen = useRef(0);
  const focused = useRef(null);
  const busy = useRef(false);
  const lastTrig = useRef(0);

  const loadOlder = async (force = false) => {
    const el = box.current;
    if (!el || busy.current || !hasMore || !msgs.length) return;
    const now = Date.now();
    if (!force && now - lastTrig.current < THROTTLE_MS) return;
    lastTrig.current = now;
    busy.current = true;
    setLoading(true);
    const h = el.scrollHeight, top = el.scrollTop;
    try { await onMore(); } finally { busy.current = false; setLoading(false); }
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight - h + top; });   // 旧消息插在上方：保持视口不跳
  };

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    if (focusId && focusId !== focused.current && msgs.some((m) => m.id === focusId)) {
      focused.current = focusId;
      const node = document.getElementById(`msg-${focusId}`);
      if (node) { node.scrollIntoView({ block: "center" }); setHl(focusId); setTimeout(() => setHl(null), 2200); }
      lastLen.current = msgs.length;
      return;
    }
    if (msgs.length > lastLen.current && stick) el.scrollTop = el.scrollHeight;
    lastLen.current = msgs.length;
  }, [msgs, focusId, stick]);

  useEffect(() => { focused.current = null; }, [focusId]);
  // 首屏只拉最近几条：大屏撑不满（没有滚动条就滚不上去）→ 自动补一页，直到可滚动或没有更多
  useEffect(() => {
    const el = box.current;
    if (!el || !hasMore || loading || !msgs.length) return;
    if (el.scrollHeight <= el.clientHeight + 8) loadOlder(true);
  }, [msgs, hasMore, loading]); // eslint-disable-line

  const onScroll = () => {
    const el = box.current;
    if (!el) return;
    setStick(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
    if (el.scrollTop < TOP_TRIGGER) loadOlder();
  };
  const jump = (id) => { const node = document.getElementById(`msg-${id}`); if (node) { node.scrollIntoView({ block: "center", behavior: "smooth" }); setHl(id); setTimeout(() => setHl(null), 2000); } else onJumpMissing?.(id); };
  const typers = Object.values(typing || {}).filter((t) => Date.now() - t.at < 6000).map((t) => t.name);
  let lastDay = null;
  return (
    <div className="relative flex-1 min-h-0">
      <div ref={box} onScroll={onScroll} data-testid="chat-message-list" data-count={msgs.length} className="h-full overflow-y-auto py-3 space-y-0.5">
        {loading && <p className="text-center text-[11px] text-slate-400 py-1"><Loader2 className="h-3.5 w-3.5 animate-spin inline" /></p>}
        {!hasMore && msgs.length > 0 && <p className="text-center text-[10px] text-slate-300 py-1">{zh ? "— 已到最早消息 —" : "— beginning —"}</p>}
        {msgs.length === 0 && <div className="text-center text-xs text-slate-400 py-16 px-6" data-testid="chat-empty">{chType === "dm" ? (zh ? "还没有聊过，发第一条消息吧。" : "No messages yet — say hi.") : (zh ? "暂无消息，发第一条吧。消息里提到项目编号 / 地址会自动归档到项目。" : "No messages yet. Mention a project id / address to auto-archive.")}</div>}
        {msgs.map((m) => {
          const dk = dayKey(m.createdAt);
          const sep = dk !== lastDay;
          lastDay = dk;
          return (
            <React.Fragment key={m.id}>
              {sep && <div className="flex items-center gap-2 px-5 py-2" data-testid={`chat-day-${dk}`}><span className="flex-1 h-px bg-slate-200/80" /><span className="text-[10px] font-semibold text-slate-400 rounded-full border border-slate-200 bg-white px-2 py-0.5">{dayLabel(dk, zh)}</span><span className="flex-1 h-px bg-slate-200/80" /></div>}
              {m.type === "system"
                ? <div id={`msg-${m.id}`} data-testid={`chat-sys-${m.id}`} data-kind={m.systemKind || ""} data-seq={m.seq} className={`flex justify-center px-5 py-1 ${hl === m.id ? "bg-amber-50" : ""}`} title={m.createdAt}>
                    <span className="max-w-[92%] text-center text-[10px] leading-snug text-slate-400 bg-slate-100/80 rounded-full px-3 py-1 inline-flex items-center gap-1"><Info className="h-3 w-3 shrink-0 opacity-70" /><span className="truncate sm:whitespace-normal">{m.body}</span><span className="text-slate-300 shrink-0">· {fmtTime(m.createdAt)}</span></span>
                  </div>
                : <MessageItem m={m} zh={zh} highlight={hl === m.id} selected={!!selectedIds?.includes(m.id)} onJump={jump} chType={chType} {...rest} />}
            </React.Fragment>
          );
        })}
      </div>
      {typers.length > 0 && <div data-testid="chat-typing" className="absolute left-5 bottom-1 text-[10px] text-slate-400 bg-white/90 rounded-full px-2 py-0.5">{typers.join("、")} {zh ? "正在输入…" : "typing…"}</div>}
      {!stick && <button type="button" data-testid="chat-scroll-bottom" onClick={() => { const el = box.current; el.scrollTop = el.scrollHeight; setStick(true); }} className="absolute right-4 bottom-3 h-8 w-8 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center text-slate-500 hover:text-[#4F6BFF]"><ArrowDown className="h-4 w-4" /></button>}
    </div>
  );
};
